// Розумний пошук запчастин: розбір вільного запиту типу
// «двигатель грейт вол ховер 2,4» або «дверь задняя левая ниссан лиф»
// на компоненти (марка, модель, група запчастини, сторони, об'єм, стан,
// вільні ключі) та зважений скоринг оголошень замість жорсткого
// AND-фільтру по підрядках. Працює без обліку роду/відмінка (стеммер +
// префіксне правило) і з синонімами UA/RU/EN (synonyms.ts).
import { getCategoryIdsWithDescendants, resolveCategoryId } from './categories.js';
import { normalizeText, stemWord } from './stem.js';
import { synonymsFor } from './synonyms.js';
import { detectMakeModel, makeAliases, modelAliases } from './makes.js';
import { matchScopedKeywordAllScopes } from './parts-classifier.js';
// ---------------------------------------------------------------------------
// Токенізація
// ---------------------------------------------------------------------------
const TOKEN_RE = /\d+[.,]\d+|[\p{L}\p{N}]+/gu;
const NUMBER_RE = /^\d{1,2}\.\d$/;
export function tokenize(raw) {
    const tokens = [];
    for (const match of raw.matchAll(TOKEN_RE)) {
        const rawToken = match[0];
        const isDecimal = /\d[.,]\d/.test(rawToken);
        const norm = isDecimal ? rawToken.replace(',', '.') : normalizeText(rawToken);
        tokens.push({ raw: rawToken, norm, stem: isDecimal ? norm : stemWord(norm) });
    }
    return tokens;
}
// ---------------------------------------------------------------------------
// Розбір запиту
// ---------------------------------------------------------------------------
const SIDE_PREFIXES = [
    ['задн', 'rear'],
    ['зад', 'rear'],
    ['передн', 'front'],
    ['перед', 'front'],
    ['лів', 'left'],
    ['лев', 'left'],
    ['прав', 'right'],
];
const SIDE_STEMS = {
    front: ['передн', 'перед'],
    rear: ['задн', 'зад'],
    left: ['лів', 'лев'],
    right: ['прав'],
};
const STOP_WORDS = new Set([
    'на',
    'для',
    'под',
    'під',
    'від',
    'от',
    'из',
    'із',
    'і',
    'и',
    'в',
    'у',
    'з',
    'со',
    'с',
    'до',
    'по',
    'за',
    'без',
    'the',
    'a',
    'шт',
    'продаю',
    'продам',
    'срочно',
]);
/** Розбирає вільний запит користувача на семантичні компоненти. */
export function parseQuery(raw) {
    const tokens = tokenize(raw);
    const consumed = new Set();
    const result = { raw, tokens, sides: [], keywords: [], structured: false };
    // 1) Марка й модель — першими, вони з'їдають багатотокенні вікна
    //    («грейт вол» + «ховер»), які не повинні розчинитися в ключах.
    const detected = detectMakeModel(tokens.map((t) => t.norm));
    if (detected.make) {
        result.make = { value: detected.make.name, tokenIdx: detected.makeIdx };
        for (const i of detected.makeIdx)
            consumed.add(i);
    }
    if (detected.model) {
        result.model = {
            make: detected.model.make,
            name: detected.model.name,
            tokenIdx: detected.modelIdx,
        };
        for (const i of detected.modelIdx)
            consumed.add(i);
    }
    // 2) Об'єм двигуна: десятковий токен на кшталт «2.4» (кома вже нормалізована)
    for (let i = 0; i < tokens.length; i++) {
        if (consumed.has(i))
            continue;
        if (NUMBER_RE.test(tokens[i].norm)) {
            result.volume = { value: tokens[i].norm, tokenIdx: [i] };
            consumed.add(i);
            break;
        }
    }
    // 3) Сторони (задня/передня/ліва/права в будь-якому роді й мові)
    for (let i = 0; i < tokens.length; i++) {
        if (consumed.has(i))
            continue;
        const stem = tokens[i].stem;
        for (const [prefix, side] of SIDE_PREFIXES) {
            if (stem === prefix || (prefix.length >= 4 && stem.startsWith(prefix))) {
                result.sides.push({ value: side, tokenIdx: [i] });
                consumed.add(i);
                break;
            }
        }
    }
    // 4) Стан: новий → new; б/у, вживаний, подорожжаний... → used
    for (let i = 0; i < tokens.length; i++) {
        if (consumed.has(i))
            continue;
        const { norm, stem } = tokens[i];
        if (stem === 'нов') {
            result.condition = { value: 'new', tokenIdx: [i] };
            consumed.add(i);
        }
        else if (stem === 'вживан' || stem === 'подержан' || norm === 'бу') {
            result.condition = { value: 'used', tokenIdx: [i] };
            consumed.add(i);
        }
        else if (norm === 'б' && tokens[i + 1]?.norm === 'у') {
            result.condition = { value: 'used', tokenIdx: [i, i + 1] };
            consumed.add(i);
            consumed.add(i + 1);
        }
    }
    // 5) Група запчастини: найдовший ключ словників груп у решті тексту.
    //    Збіг шукаємо підрядком по нормалізованих токенах (як classifyScoped),
    //    тому пам'ятаємо зміщення кожного токена у зібраному рядку.
    let rest = '';
    const offsets = [];
    for (let i = 0; i < tokens.length; i++) {
        if (consumed.has(i))
            continue;
        offsets.push({ start: rest.length, len: tokens[i].norm.length, idx: i });
        rest += tokens[i].norm;
        rest += ' ';
    }
    const groupHit = rest.trim() ? matchScopedKeywordAllScopes(rest.trim()) : null;
    if (groupHit) {
        const pos = rest.indexOf(groupHit.kw);
        const end = pos + groupHit.kw.length;
        const tokenIdx = offsets
            .filter((o) => o.start < end && o.start + o.len > pos)
            .map((o) => o.idx);
        if (tokenIdx.length) {
            result.group = { id: groupHit.id, keyword: groupHit.kw, tokenIdx };
            for (const i of tokenIdx)
                consumed.add(i);
        }
    }
    // 6) Решта — вільні ключові слова
    for (let i = 0; i < tokens.length; i++) {
        if (consumed.has(i))
            continue;
        const norm = tokens[i].norm;
        if (norm.length < 2 || STOP_WORDS.has(norm))
            continue;
        result.keywords.push({ tokenIdx: i, stem: tokens[i].stem });
    }
    result.structured = Boolean(result.make || result.model || result.group || result.volume || result.condition || result.sides.length);
    return result;
}
/** Рядок запиту без компонентів, з'їдених чіпсом (токени перезбираються через пробіл). */
export function removeComponentFromQuery(parsed, tokenIdx) {
    const drop = new Set(tokenIdx);
    return parsed.tokens
        .filter((_, i) => !drop.has(i))
        .map((t) => t.raw)
        .join(' ')
        .trim();
}
export function buildHaystack(src) {
    const titleTokens = tokenize(src.title ?? '');
    const restTokens = tokenize(`${src.description ?? ''} ${src.wanted ?? ''} ${src.make ?? ''} ${src.model ?? ''}`);
    const all = [...titleTokens, ...restTokens];
    return {
        make: src.make ?? null,
        model: src.model ?? null,
        categoryId: src.categoryId ?? null,
        condition: src.condition ?? null,
        titleNorm: titleTokens.map((t) => t.norm).join(' '),
        titleStems: titleTokens.map((t) => t.stem),
        allNorm: all.map((t) => t.norm).join(' '),
        allStems: all.map((t) => t.stem),
    };
}
// ---------------------------------------------------------------------------
// Матчинг
// ---------------------------------------------------------------------------
function stemHit(stems, queryStem) {
    for (const h of stems) {
        if (h === queryStem)
            return true;
        // префіксний збіг з мінімальною довжиною 4, щоб «масл» не чіпляв «мости»
        if (queryStem.length >= 4 && h.startsWith(queryStem))
            return true;
        if (h.length >= 4 && queryStem.startsWith(h))
            return true;
    }
    return false;
}
/** Збіг фрази/аліаса по межах слів у нормалізованому тексті. */
function phraseHit(normText, phrase) {
    if (!phrase || !normText)
        return false;
    if (normText === phrase)
        return true;
    return (normText.startsWith(phrase + ' ') ||
        normText.endsWith(' ' + phrase) ||
        normText.includes(' ' + phrase + ' '));
}
/**
 * Зважений збіг оголошення з розібраним запитом. Сильні компоненти дають
 * включення до результату, брак дрібних — м'який штраф, а не відсіч:
 * «капот бмв е39» має знайти і «Капот BMW 5 (E39)».
 */
export function scoreAd(parsed, h) {
    let score = 0;
    let partHit = false;
    let whoHit = false;
    if (parsed.make) {
        const canon = normalizeText(parsed.make.value);
        if (h.make && normalizeText(h.make) === canon) {
            score += 6;
            whoHit = true;
        }
        else if (makeAliases(parsed.make.value).some((a) => phraseHit(h.allNorm, a))) {
            score += 5;
            whoHit = true;
        }
        else {
            score -= 1.5;
        }
    }
    if (parsed.model) {
        const nameNorm = normalizeText(parsed.model.name);
        const inField = Boolean(h.model && normalizeText(h.model).includes(nameNorm));
        const inText = modelAliases(parsed.model.make, parsed.model.name).some((a) => phraseHit(h.allNorm, a));
        if (inField || inText) {
            score += 4;
            whoHit = true;
        }
        else {
            score -= 1;
        }
    }
    if (parsed.group) {
        const catOk = Boolean(h.categoryId) &&
            getCategoryIdsWithDescendants(parsed.group.id).includes(resolveCategoryId(h.categoryId));
        if (catOk) {
            score += 3;
            partHit = true;
        }
        else {
            // ключ груп — часто стеми («масл»), тому підрядок, а не фраза;
            // перше слово ключа ще й розгортається синонімами, щоб «мотор»
            // знайшов «двигун» навіть без збігу категорії
            const variants = synonymsFor(parsed.group.keyword.split(' ')[0]);
            if (variants.some((v) => stemHit(h.allStems, v)) || h.allNorm.includes(parsed.group.keyword)) {
                score += 2;
                partHit = true;
            }
        }
    }
    for (const kw of parsed.keywords) {
        const variants = synonymsFor(kw.stem);
        if (variants.some((v) => stemHit(h.allStems, v))) {
            partHit = true;
            score += variants.some((v) => stemHit(h.titleStems, v)) ? 3 : 2;
        }
        else {
            score -= 0.5;
        }
    }
    for (const side of parsed.sides) {
        const hit = SIDE_STEMS[side.value].some((s) => stemHit(h.allStems, s));
        score += hit ? 1.5 : -0.75;
    }
    if (parsed.volume) {
        score += h.allStems.includes(parsed.volume.value) ? 1.5 : -0.75;
    }
    if (parsed.condition) {
        if (h.condition === parsed.condition.value)
            score += 1.5;
        else if (h.condition)
            score -= 0.5;
    }
    const queryHasPart = Boolean(parsed.group) || parsed.keywords.length > 0;
    const matched = queryHasPart ? partHit : whoHit;
    return { score, matched };
}
//# sourceMappingURL=smart-search.js.map